import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { Box, Typography, CircularProgress, Grid, Card, CardContent } from '@material-ui/core';
import { ErrorOutline, CheckCircle } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { PublicScreenWrapper } from '../PublicScreenWrapper';
import { RefereeCheckForm } from './RefereeCheckForm';
import { RefereeCheckCompleted } from './RefereeCheckCompleted';
import { useRefereeCheckActions } from './referee-check-actions';
import { useApiUrl } from '../../../global/config/useApiUrl';
import { useCampfireQuery } from '../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { GET_ORG_NAME } from '../login/sign-in-model.gql';
import { GetOrgName } from '../login/__generated__/GetOrgName';

interface RefereeSubmissionData {
  id: string;
  completedTaskId: string;
  refereeName: string;
  refereeEmail: string;
  isCompleted: boolean;
  dateInvited: string | null;
  dateSubmitted: string | null;
  submissionToken: string;
  refereeMessage: string | null;
  task: {
    title: string;
    instructions: string | null;
    taskFields: Array<{
      taskFieldId: string;
      fieldName: string;
      fieldType: string;
      isRequired: boolean;
      selectOptions: string[] | null;
      allowsMultiple: boolean;
      placeholder: string | null;
      helpText: string | null;
    }>;
  };
  volunteer: {
    firstName: string;
    lastName: string;
    preferredName: string | null;
  } | null;
}

interface OrgLogoHeaderProps {
  data?: GetOrgName;
  loading: boolean;
  volunteerName?: string;
}

const OrgLogoHeader: React.FC<OrgLogoHeaderProps> = ({ data, loading, volunteerName }) => {
  if (loading) {
    return (
      <Box textAlign='center' marginBottom={4}>
        <Skeleton variant='rect' height={65} width={200} style={{ margin: '0 auto', marginBottom: 16 }} />
        <Skeleton variant='text' height={28} width={300} style={{ margin: '0 auto' }} />
      </Box>
    );
  }

  return (
    <Box marginBottom={3} paddingLeft={1}>
      {data?.orgLogo && (
        <Box marginBottom={3}>
          <img
            src={data.orgLogo}
            alt={data.orgName}
            style={{
              height: 65,
              width: 'auto',
              display: 'block',
            }}
          />
        </Box>
      )}
      <Typography
        variant='h5'
        style={{
          color: '#3E3229',
          fontSize: '22px',
          fontWeight: 700,
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
          marginBottom: 4,
        }}
      >
        Reference Request for {volunteerName}
      </Typography>
      {data?.orgName && (
        <Typography
          variant='body1'
          style={{
            color: '#646464',
            fontSize: '16px',
            fontWeight: 400,
          }}
        >
          {data.orgName}
        </Typography>
      )}
    </Box>
  );
};

export function RefereeCheckScreen() {
  const { token } = useParams<{ token: string }>();
  const [submissionData, setSubmissionData] = useState<RefereeSubmissionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const isMountedRef = useRef(true);

  const { getRefereeSubmission, submitRefereeData } = useRefereeCheckActions();
  const apiUrl = useApiUrl();
  const { isMobile } = useCampfireTheme();

  // Helper function to get volunteer display name
  const getVolunteerName = (volunteer: RefereeSubmissionData['volunteer'] | undefined) => {
    if (!volunteer) return undefined;
    return volunteer.preferredName || `${volunteer.firstName} ${volunteer.lastName}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Get organization info for logo and name
  const { data: orgData, loading: orgLoading } = useCampfireQuery<GetOrgName, null>(GET_ORG_NAME, {
    options: { skip: !token }, // Only fetch org data if we have a token
  });

  useEffect(() => {
    // Only make the API call once when component mounts with a valid token
    // Don't make API calls if we don't have a token
    if (!token) {
      if (isMountedRef.current) {
        setError('missing_token');
        setLoading(false);
      }
    } else {
      getRefereeSubmission
        .run({
          url: `${apiUrl}/vm/task/referee-check/get?submissionToken=${encodeURIComponent(token)}`,
          method: 'get',
        })
        .then((response) => {
          if (!isMountedRef.current) return;

          if (response.ok && response.data?.data?.refereeContact) {
            setSubmissionData(response.data.data.refereeContact);
          } else {
            setError('invalid_token');
          }
        })
        .catch((err) => {
          if (!isMountedRef.current) return;
          console.error('Error loading referee submission:', err);
          setError('network_error');
        })
        .finally(() => {
          if (isMountedRef.current) {
            setLoading(false);
          }
        });
    }
  }, [token, apiUrl]);

  const handleSubmit = async (fieldValues: Array<{ taskFieldId: string; value: string }>) => {
    if (!token) return;

    try {
      const result = await submitRefereeData.run({
        url: `${apiUrl}/vm/task/referee-check/submit`,
        method: 'post',
        data: {
          submissionToken: token,
          fieldValues: fieldValues,
        },
      });

      if (result.data?.data?.success) {
        setSubmitted(true);
      } else {
        setError('submission_failed');
      }
    } catch (err) {
      setError('submission_failed');
    }
  };

  // Helper function to get user-friendly error messages
  const getErrorMessage = (errorCode: string) => {
    switch (errorCode) {
      case 'missing_token':
        return {
          title: 'Invalid Link',
          description:
            'This reference link appears to be incomplete or corrupted. Please check that you have copied the entire link from your email.',
        };
      case 'invalid_token':
        return {
          title: 'Reference Not Found',
          description:
            'This reference link could not be found. Please check that you have copied the entire link from your email, or contact the organisation for assistance.',
        };
      case 'network_error':
        return {
          title: 'Connection Problem',
          description:
            "We're having trouble connecting to our servers. Please check your internet connection and try again.",
        };
      case 'submission_failed':
        return {
          title: 'Submission Failed',
          description:
            "We weren't able to save your reference. Please try again, or contact the organisation for assistance.",
        };
      default:
        return {
          title: 'Something Went Wrong',
          description: 'We encountered an unexpected problem. Please try again or contact the organisation for help.',
        };
    }
  };

  if (loading) {
    return (
      <PublicScreenWrapper hideHeader>
        <Box marginTop={isMobile ? 3 : 7} marginBottom={8} marginLeft={2} marginRight={2}>
          <Grid container justify='center'>
            <Grid item xs={12} sm={10}>
              <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
                <CircularProgress />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </PublicScreenWrapper>
    );
  }

  if (error) {
    const errorMessage = getErrorMessage(error);
    return (
      <PublicScreenWrapper hideHeader>
        <Box display='flex' alignItems='center' justifyContent='center' minHeight='60vh' marginLeft={2} marginRight={2}>
          <Grid container justify='center' spacing={3}>
            <Grid item xs={12} sm={10}>
              <Box display='flex' justifyContent='center' alignItems='center' marginBottom={2}>
                <ErrorOutline style={{ fontSize: 48, color: '#f44336', marginRight: 8 }} />
              </Box>
              <Typography variant='h5' style={{ fontWeight: 'bold' }} align='center' gutterBottom>
                {errorMessage.title}
              </Typography>
              <Typography variant='body1' align='center' color='textSecondary' style={{ marginBottom: 24 }}>
                {errorMessage.description}
              </Typography>
              <Typography variant='body2' align='center' color='textSecondary'>
                If you continue to have problems, please contact the organisation that sent you this link.
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </PublicScreenWrapper>
    );
  }

  if (submitted) {
    return (
      <PublicScreenWrapper hideHeader>
        <Box display='flex' alignItems='center' justifyContent='center' minHeight='60vh' marginLeft={2} marginRight={2}>
          <Box>
            <OrgLogoHeader
              data={orgData}
              loading={orgLoading}
              volunteerName={getVolunteerName(submissionData?.volunteer)}
            />
            <RefereeCheckCompleted volunteerName={getVolunteerName(submissionData?.volunteer) || ''} />
          </Box>
        </Box>
      </PublicScreenWrapper>
    );
  }

  if (submissionData?.isCompleted) {
    return (
      <PublicScreenWrapper hideHeader>
        <Box display='flex' alignItems='center' justifyContent='center' minHeight='60vh' marginLeft={2} marginRight={2}>
          <Box>
            <OrgLogoHeader
              data={orgData}
              loading={orgLoading}
              volunteerName={getVolunteerName(submissionData?.volunteer)}
            />
            <Box p={2}>
              <Card style={{ textAlign: 'center', padding: '24px', backgroundColor: 'transparent', boxShadow: 'none' }}>
                <CardContent>
                  <CheckCircle style={{ fontSize: '40px', color: '#4caf50', marginBottom: '12px' }} />

                  <Typography
                    variant='h5'
                    gutterBottom
                    style={{ fontWeight: 600, color: '#3E3229', marginBottom: '8px' }}
                  >
                    Reference Already Submitted
                  </Typography>

                  <Typography variant='body1' gutterBottom style={{ color: '#3E3229', marginBottom: '16px' }}>
                    Thank you! Your reference for <strong>{getVolunteerName(submissionData?.volunteer)}</strong> has
                    already been submitted.
                  </Typography>

                  <Typography variant='body2' color='textSecondary'>
                    You may now close this window.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </PublicScreenWrapper>
    );
  }

  if (!submissionData) {
    const errorMessage = getErrorMessage('invalid_token');
    return (
      <PublicScreenWrapper hideHeader>
        <Box display='flex' alignItems='center' justifyContent='center' minHeight='60vh' marginLeft={2} marginRight={2}>
          <Grid container justify='center' spacing={3}>
            <Grid item xs={12} sm={10}>
              <Box display='flex' justifyContent='center' alignItems='center' marginBottom={2}>
                <ErrorOutline style={{ fontSize: 48, color: '#f44336', marginRight: 8 }} />
              </Box>
              <Typography variant='h5' style={{ fontWeight: 'bold' }} align='center' gutterBottom>
                {errorMessage.title}
              </Typography>
              <Typography variant='body1' align='center' color='textSecondary'>
                {errorMessage.description}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </PublicScreenWrapper>
    );
  }

  return (
    <PublicScreenWrapper hideHeader shrink={isMobile ? true : 1000}>
      <Box
        marginTop={isMobile ? 1 : 2}
        marginBottom={0}
        paddingX={isMobile ? 2 : 4}
        paddingTop={isMobile ? 2 : 3}
        paddingBottom={isMobile ? 2 : 3}
      >
        <OrgLogoHeader data={orgData} loading={orgLoading} volunteerName={getVolunteerName(submissionData.volunteer)} />
        <RefereeCheckForm
          volunteerName={getVolunteerName(submissionData.volunteer) || ''}
          taskTitle={submissionData.task.title}
          instructions={submissionData.task.instructions}
          taskFields={submissionData.task.taskFields}
          refereeMessage={submissionData.refereeMessage}
          onSubmit={handleSubmit}
          isSubmitting={submitRefereeData.isLoading}
          orgLogo={orgData?.orgLogo}
          orgName={orgData?.orgName}
        />
      </Box>
    </PublicScreenWrapper>
  );
}
